import { Camera, School, Leaf, MapPin, Fish, Compass, Bird, Mountain, TreePine, Waves, Heart, Binoculars } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ExperienceCategory {
  id: string;
  name: string;
  emoji: string;
  icon: LucideIcon;
  description: string;
  type: 'animal' | 'adventure' | 'both';
  image?: string;
}

/**
 * Unified experience categories used across the application
 * Each category includes both emoji and Lucide icons for flexibility
 * Categories are typed as 'animal', 'adventure', or 'both' for filtering
 */
export const EXPERIENCE_CATEGORIES: ExperienceCategory[] = [
  // Animal Activities
  {
    id: 'safari',
    name: 'Wildlife Safari',
    emoji: '🦁',
    icon: Compass,
    description: 'Guided tours to observe wildlife in their natural habitat',
    type: 'animal',
    image: '/images/experiences/safari.jpg'
  },
  {
    id: 'whale_watching',
    name: 'Whale Watching',
    emoji: '🐋',
    icon: Binoculars,
    description: 'Observe majestic whales and marine mammals in their natural environment',
    type: 'animal',
    image: '/images/experiences/whale-watching.jpg'
  },
  {
    id: 'birdwatching',
    name: 'Bird Watching',
    emoji: '🦜',
    icon: Bird,
    description: 'Discover diverse bird species in their natural habitats',
    type: 'animal',
    image: '/images/experiences/bird-watching.jpg'
  },
  {
    id: 'underwater',
    name: 'Marine Wildlife',
    emoji: '🤿',
    icon: Fish,
    description: 'Snorkeling or diving to observe underwater marine life',
    type: 'animal',
    image: '/images/experiences/diving.jpg'
  },
  {
    id: 'conservation',
    name: 'Conservation Activities',
    emoji: '🌱',
    icon: Leaf,
    description: 'Participate in wildlife conservation and protection efforts',
    type: 'animal'
  },
  {
    id: 'sanctuary',
    name: 'Wildlife Sanctuary',
    emoji: '🦒',
    icon: MapPin,
    description: 'Visit protected areas for rescued and rehabilitated animals',
    type: 'animal'
  },
  {
    id: 'photography',
    name: 'Wildlife Photography',
    emoji: '📸',
    icon: Camera,
    description: 'Specialized tours for capturing stunning wildlife moments',
    type: 'both',
    image: '/images/experiences/photography.jpg'
  },

  // Adventure Activities
  {
    id: 'hiking',
    name: 'Hiking Tours',
    emoji: '🥾',
    icon: Mountain,
    description: 'Trek through scenic wildlife territories and natural landscapes',
    type: 'adventure',
    image: '/images/experiences/hiking.jpg'
  },
  {
    id: 'kayaking',
    name: 'Kayaking',
    emoji: '🛶',
    icon: Waves,
    description: 'Paddle through wildlife-rich waters and pristine waterways',
    type: 'adventure',
    image: '/images/experiences/kayaking.jpg'
  },
  {
    id: 'camping',
    name: 'Wildlife Camping',
    emoji: '⛺',
    icon: TreePine,
    description: 'Immerse in nature with overnight camping experiences',
    type: 'adventure',
    image: '/images/experiences/camping.jpg'
  },
  {
    id: 'jeep_safari',
    name: 'Adventure Safari',
    emoji: '🚙',
    icon: Compass,
    description: 'Off-road adventures through wilderness areas',
    type: 'adventure'
  },

  // Both Animal & Adventure
  {
    id: 'education',
    name: 'Educational Programs',
    emoji: '📚',
    icon: School,
    description: 'Learn about wildlife, ecosystems, and conservation',
    type: 'both'
  }
];

/**
 * Helper function to filter experience categories by activity type
 */
export const getExperienceCategoriesByType = (
  hasAnimalActivities: boolean,
  hasAdventureActivities: boolean
): ExperienceCategory[] => {
  return EXPERIENCE_CATEGORIES.filter(category => {
    // If neither activity type is selected, show all (shouldn't happen in normal flow)
    if (!hasAnimalActivities && !hasAdventureActivities) {
      return true;
    }
    
    // For categories marked as 'both', always show if at least one activity type is selected
    if (category.type === 'both') {
      return true;
    }
    
    // Show animal categories if animal activities selected
    if (category.type === 'animal' && hasAnimalActivities) {
      return true;
    }
    
    // Show adventure categories if adventure activities selected
    if (category.type === 'adventure' && hasAdventureActivities) {
      return true;
    }
    
    return false;
  });
};

/**
 * Helper function to get experience category by ID
 */
export const getExperienceCategoryById = (id: string): ExperienceCategory | undefined => {
  return EXPERIENCE_CATEGORIES.find(category => category.id === id);
};

/**
 * Legacy ID mappings for backward compatibility
 * Maps old homepage category IDs to new unified IDs
 */
export const LEGACY_ID_MAPPINGS: Record<string, string> = {
  'whale': 'whale_watching',
  'bird': 'birdwatching',
  'diving': 'underwater'
};

/**
 * Helper function to get category by legacy ID (for backward compatibility)
 */
export const getCategoryByLegacyId = (legacyId: string): ExperienceCategory | undefined => {
  const newId = LEGACY_ID_MAPPINGS[legacyId] || legacyId;
  return getExperienceCategoryById(newId);
}; 